import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { DataTable, type DataColumn } from '@/shared/ui/DataTable';
import { DetailBox } from '@/shared/ui/DetailLayout';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { PromocodeGenerateModal } from './PromocodeGenerateModal';
import { PromocodeModal } from './PromocodeModal';
import {
  emptyPromocode,
  fetchDiscountLevels,
  fetchPromocodes,
  fetchPromoTypes,
  promocodeToValues,
  type Promocode,
} from './promoSectionsApi';

type Dialog = { kind: 'create' } | { kind: 'generate' } | { kind: 'edit'; item: Promocode } | null;

export const PromocodesSection = ({ promoId }: { promoId: number }) => {
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<Dialog>(null);

  const list = useQuery({
    queryKey: ['promocodes', promoId, page],
    queryFn: () => fetchPromocodes(promoId, page),
  });
  const types = useQuery({ queryKey: ['promo-types'], queryFn: fetchPromoTypes });
  const levels = useQuery({ queryKey: ['discount-levels'], queryFn: fetchDiscountLevels });

  const columns: DataColumn<Promocode>[] = [
    { header: 'ID', render: (item) => item.id },
    { header: 'Promo ID', render: (item) => item.promo_id },
    { header: 'Дата, время завершения', render: (item) => formatUnixDateTime(item.expired_time) },
    { header: 'Дата, время сгорания', render: (item) => formatUnixDateTime(item.burn_time) },
    { header: 'Code', render: (item) => item.code },
    { header: 'Сумма', render: (item) => item.amount },
    { header: 'Количество', render: (item) => item.count },
    { header: 'Количество использований', render: (item) => item.count_of_uses },
    { header: 'Type ID', render: (item) => item.type_id },
    { header: 'Валюта', render: (item) => item.currency?.code },
    {
      header: 'Edit',
      render: (item) => (
        <button type="button" className="btn btn-success btn-sm" onClick={() => setDialog({ kind: 'edit', item })}>
          <i className="fa fa-pencil" />
        </button>
      ),
    },
  ];

  const modalProps = {
    promoId,
    types: types.data ?? [],
    discountLevels: levels.data ?? [],
    onClose: () => setDialog(null),
  };

  return (
    <DetailBox title="Промокоды">
      <div className="clearfix" style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-info pull-right" onClick={() => setDialog({ kind: 'create' })}>
          Создать промокод
        </button>
        <button
          type="button"
          className="btn btn-info pull-right"
          style={{ marginRight: 15 }}
          onClick={() => setDialog({ kind: 'generate' })}
        >
          Сгенерировать промокод
        </button>
      </div>
      <QueryStatus isLoading={list.isLoading} error={list.error} errorText="Ошибка загрузки промокодов" />
      <DataTable columns={columns} rows={list.data?.items ?? []} rowKey={(item) => item.id} />
      <Pagination page={page} totalPages={list.data?.totalPages ?? 1} onChange={setPage} />

      {dialog?.kind === 'create' && <PromocodeModal {...modalProps} initial={emptyPromocode()} />}
      {dialog?.kind === 'edit' && (
        <PromocodeModal {...modalProps} id={dialog.item.id} initial={promocodeToValues(dialog.item)} />
      )}
      {dialog?.kind === 'generate' && <PromocodeGenerateModal promoId={promoId} onClose={modalProps.onClose} />}
    </DetailBox>
  );
};
